import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiTrendingUp, FiAward, FiShield, FiUsers, FiTarget } = FiIcons;

const AboutPage = () => {
  const timeline = [
    {
      year: '1985',
      title: 'Company Founded',
      description: 'Started as a small machine shop serving local mining operations in Gauteng.'
    },
    {
      year: '1990s',
      title: 'Automotive Expansion',
      description: 'Diversified into automotive component manufacturing, securing major contracts.'
    },
    {
      year: '2000s',
      title: 'ISO Certification',
      description: 'Achieved ISO 9001:2015 certification and expanded into renewable energy components.'
    },
    {
      year: 'Today',
      title: 'Industry Leader',
      description: '15 hectares of advanced facilities serving major clients across Southern Africa.'
    }
  ];

  const values = [
    {
      icon: FiAward,
      title: 'Quality Excellence',
      description: 'Every component meets rigorous quality standards with comprehensive testing protocols.'
    },
    {
      icon: FiShield,
      title: 'Safety First',
      description: 'Zero-compromise approach to workplace safety and industrial standards compliance.'
    },
    {
      icon: FiUsers,
      title: 'Client Partnership',
      description: 'Long-standing relationships built on trust, reliability, and exceptional service.'
    },
    {
      icon: FiTarget,
      title: 'Precision Focus',
      description: 'Delivering components with tolerances to ±0.005mm using advanced technology.'
    }
  ];

  const certifications = [
    'ISO 9001:2015 Quality Management',
    'SANS Compliance',
    'ASME Pressure Vessel Code',
    'AWS Welding Standards',
    'API Specifications',
    'OHSAS 18001 Safety Management'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">About Gauteng Manufacturing</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto text-gray-200">
              Four decades of South African industrial excellence, delivering precision-engineered 
              solutions that power the nation's economy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company History */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Journey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From humble beginnings to industry leadership, our story is one of continuous 
              innovation, unwavering quality, and commitment to South African manufacturing excellence.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-200"></div>
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}>
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <div className="text-2xl font-bold text-blue-800 mb-2">{item.year}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                  <div className="w-6 h-6 bg-orange-500 rounded-full border-4 border-white shadow-lg z-10"></div>
                  <div className="w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide our operations and define our commitment to excellence 
              in every project we undertake.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={value.icon} className="text-2xl text-blue-800" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Quality Commitment</h2>
              <p className="text-lg text-gray-600 mb-6">
                Every component leaving our facilities meets rigorous quality standards verified 
                through comprehensive testing protocols. Our quality management system ensures 
                consistency across all production lines whilst maintaining flexibility to 
                accommodate custom requirements.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                We combine traditional manufacturing expertise with cutting-edge technology to 
                meet the evolving demands of modern industry, ensuring our clients receive 
                components that exceed expectations in both quality and performance.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Certifications & Standards</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <SafeIcon icon={FiAward} className="text-orange-500" />
                      <span className="text-gray-700 text-sm">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-blue-100 to-orange-100 rounded-2xl p-8 h-96 flex items-center justify-center">
                <div className="text-center">
                  <SafeIcon icon={FiShield} className="text-8xl text-blue-800 mb-4 mx-auto" />
                  <p className="text-gray-600 font-medium">Quality Assurance Excellence</p>
                  <p className="text-gray-500 text-sm mt-2">ISO 9001:2015 Certified Operations</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">World-Class Facilities</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our state-of-the-art facilities in Germiston and Vanderbijlpark span 15 hectares, 
              featuring advanced CNC machining centers, automated welding systems, and 
              comprehensive quality assurance laboratories.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gray-800 rounded-xl p-8 text-center"
            >
              <SafeIcon icon={FiTrendingUp} className="text-4xl text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-4">Advanced Technology</h3>
              <p className="text-gray-300">
                5-axis CNC machining centers with precision capabilities to ±0.005mm tolerances.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-gray-800 rounded-xl p-8 text-center"
            >
              <SafeIcon icon={FiUsers} className="text-4xl text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-4">Skilled Workforce</h3>
              <p className="text-gray-300">
                Experienced technicians and engineers with decades of manufacturing expertise.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gray-800 rounded-xl p-8 text-center"
            >
              <SafeIcon icon={FiCalendar} className="text-4xl text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-4">24/7 Operations</h3>
              <p className="text-gray-300">
                Round-the-clock production capabilities to meet urgent project deadlines.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;